import Phaser from 'phaser';
import { getAllStudentsAdmin, type AdminStudentRow } from '@/systems/AdminService';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';

/**
 * AdminPanelScene — visão global pro pai com is_admin=true.
 *
 * Lista todos os alunos cadastrados ordenados por XP. Renderizado via
 * scene.add.dom em <table> HTML porque Phaser não tem widget de tabela
 * pronta e construir grid em canvas pra dezenas de linhas é caro
 * visualmente. Tabela é responsiva, scrollável, e suporta seleção/
 * cópia nativa do navegador (útil pra exportar pra planilha).
 */
export class AdminPanelScene extends Phaser.Scene {
  private statusText?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'AdminPanelScene' });
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    const { width, height } = this.scale;

    this.add
      .text(width / 2, 60, '⚙️ Painel Admin', {
        fontFamily: Typography.fontFamily,
        fontSize: '40px',
        color: Palette.warningCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, 104, 'Todos os alunos cadastrados, ordenados por XP', {
        fontFamily: Typography.fontFamily,
        fontSize: '18px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    new Button(this, 96, height - 48, 'Voltar', {
      width: 160,
      height: 48,
      fontSize: 18,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.primary,
      onClick: () => fadeToScene(this, 'MenuScene'),
    });

    this.statusText = this.add
      .text(width / 2, 140, 'Carregando alunos...', {
        fontFamily: Typography.fontFamily,
        fontSize: '14px',
        color: Palette.textDimCss,
      })
      .setOrigin(0.5);

    void this.loadAndRender();
  }

  private async loadAndRender(): Promise<void> {
    const result = await getAllStudentsAdmin();
    if (!result.ok) {
      const msg =
        result.error.toLowerCase().includes('forbidden') || result.error.includes('42501')
          ? 'Acesso negado — apenas admins veem este painel.'
          : `Erro: ${result.error}`;
      this.statusText?.setColor(Palette.dangerCss).setText(msg);
      return;
    }

    if (result.value.length === 0) {
      this.statusText?.setText('Nenhum aluno cadastrado ainda.');
      return;
    }

    this.statusText?.setText(`${result.value.length} aluno(s) — clique em qualquer linha pra selecionar/copiar.`);
    this.renderTable(result.value);
  }

  private renderTable(rows: AdminStudentRow[]): void {
    const { width, height } = this.scale;
    const html = `
      <div style="${wrapperStyle(width, height)}">
        <table style="${tableStyle}">
          <thead style="${theadStyle}">
            <tr>
              <th style="${thStyle}">#</th>
              <th style="${thStyle}">Aluno</th>
              <th style="${thStyle}">Responsável</th>
              <th style="${thStyle}">E-mail</th>
              <th style="${thNumberStyle}">XP</th>
              <th style="${thNumberStyle}">Recorde 60s</th>
              <th style="${thStyle}">Última jogada</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((r, i) => this.renderRow(r, i + 1)).join('')}
          </tbody>
        </table>
      </div>
    `;
    this.add.dom(width / 2, height / 2 + 40).createFromHTML(html);
  }

  private renderRow(row: AdminStudentRow, position: number): string {
    const lastPlayed = formatDate(row.lastPlayedAt);
    const parentName = `${escapeHtml(row.parentFirstName)} ${escapeHtml(row.parentLastName)}`.trim();
    return `
      <tr style="${trStyle}">
        <td style="${tdStyle}">${position}</td>
        <td style="${tdStyle}"><b>${escapeHtml(row.displayName)}</b></td>
        <td style="${tdStyle}">${parentName}</td>
        <td style="${tdStyle}">${escapeHtml(row.parentEmail)}</td>
        <td style="${tdNumberStyle}">${row.totalXP}</td>
        <td style="${tdNumberStyle}">${row.timeAttackBestScore}</td>
        <td style="${tdStyle}">${lastPlayed}</td>
      </tr>
    `;
  }
}

/* ---------- helpers ---------- */

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

/* ---------- estilos ---------- */

const wrapperStyle = (w: number, h: number) => `
  width: ${Math.min(w - 96, 1100)}px;
  max-height: ${h - 240}px;
  overflow-y: auto;
  background: rgba(20, 20, 40, 0.92);
  border: 1px solid ${Palette.textDimCss}55;
  border-radius: 12px;
  font-family: ${Typography.fontFamily};
  color: ${Palette.textPrimaryCss};
`;

const tableStyle = `
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const theadStyle = `
  position: sticky; top: 0;
  background: ${Palette.primaryCss};
`;

const thStyle = `
  padding: 12px 14px; text-align: left;
  font-weight: 700; color: ${Palette.textPrimaryCss};
  border-bottom: 1px solid ${Palette.textDimCss}55;
`;

const thNumberStyle = `
  ${thStyle.replace('text-align: left', 'text-align: right')}
`;

const trStyle = `
  border-bottom: 1px solid ${Palette.textDimCss}33;
`;

const tdStyle = `
  padding: 10px 14px; vertical-align: middle;
  color: ${Palette.textPrimaryCss};
`;

const tdNumberStyle = `
  ${tdStyle.replace('padding: 10px 14px', 'padding: 10px 14px; text-align: right')}
`;
